import { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  type PaymentMethodRowDisplay,
  getPaymentMethodRowDisplay,
} from '@fhs/cart/src/components/checkout-components/payment-details/payment-method-icon-map';
import {
  IconErrorFill,
  ListItemGroup,
  ListItemRadioWithChildren,
  RadioGroup,
  Text,
  XStack,
  YStack,
  tokens,
} from '@fhs/ui';
import { StyledAddNewCreditCardIcon } from '@fhs-legacy/frontend/src/components/payment-method/styled';
import { PaymentMethodAddNewItemOption } from '@fhs-legacy/frontend/src/components/payment-method-add-new-item-option';
import { IPaymentMethod } from '@fhs-legacy/frontend/src/state/payment/types';

type PaymentMethodSelectProps = {
  paymentMethods: IPaymentMethod[];
  selectedOption: string;
  customHeader?: ReactNode;
  onSelectOption: (value: string) => void;
};

function PaymentMethodRow({ display }: { display: PaymentMethodRowDisplay }) {
  return (
    <XStack style={styles.methodRow}>
      <View style={styles.methodIconCircle}>{display.icon}</View>
      <YStack style={styles.methodTextColumn}>
        <Text.Ui weight="semibold" size="lg">
          {display.label}
        </Text.Ui>
        {display.subLabel ? <Text.Ui size="md">{display.subLabel}</Text.Ui> : null}
        {display.expired ? (
          <XStack style={styles.expiredRow}>
            <IconErrorFill size={16} color={tokens.colors.$error} />
            <Text.Ui size="md" style={styles.expiredText}>
              Card Expired
            </Text.Ui>
          </XStack>
        ) : null}
      </YStack>
    </XStack>
  );
}

export const PaymentMethodSelect = ({
  customHeader,
  paymentMethods,
  selectedOption,
  onSelectOption,
}: PaymentMethodSelectProps) => {

  const paymentMethodItems = paymentMethods.map(method => {
    const display = getPaymentMethodRowDisplay(method);
    return {
      id: method.accountIdentifier ?? undefined,
      render: (listItemProps: { isFirstItem?: boolean }) => (
        <>
          {customHeader && listItemProps.isFirstItem && (
            <YStack style={styles.headerContainer}>{customHeader}</YStack>
          )}
          <ListItemRadioWithChildren
            value={method.accountIdentifier ?? ''}
            label={method.accountIdentifier ?? ''}
            disabled={display.expired}
            isFirstItem={customHeader ? listItemProps.isFirstItem : false}
            isLastItem={false}
            indicatorSize="sm"
          >
            <PaymentMethodRow display={display} />
          </ListItemRadioWithChildren>
        </>
      ),
    };
  });

  const addNewCardItem = {
    id: 'newCard',
    render: (listItemProps: { isFirstItem?: boolean }) => (
      <>
        {customHeader && listItemProps.isFirstItem && (
          <YStack style={styles.headerContainer}>{customHeader}</YStack>
        )}
        <ListItemRadioWithChildren
          value="newCard"
          label="newCard"
          isFirstItem={customHeader ? false : listItemProps.isFirstItem}
          isLastItem
          indicatorSize="sm"
        >
          <PaymentMethodAddNewItemOption
            text="Add New Card"
            Icon={<StyledAddNewCreditCardIcon />}
            onPress={() => {}}
          />
        </ListItemRadioWithChildren>
      </>
    ),
  };

  return (
    <RadioGroup value={selectedOption} onChange={onSelectOption}>
      <ListItemGroup items={[...paymentMethodItems, addNewCardItem]} />
    </RadioGroup>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    flex: 1,
    width: '100%',
    padding: 16,
    borderBottomWidth: 1,
    borderColor: tokens.colors.$black10,
  },
  methodRow: {
    flex: 1,
    gap: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  methodIconCircle: {
    height: 40,
    width: 40,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  methodTextColumn: {
    flexShrink: 1,
  },
  expiredRow: {
    alignItems: 'center',
    gap: 4,
  },
  expiredText: {
    color: tokens.colors.$error,
  },
});
